import { DOCUMENT } from '@angular/common';
import {
  Component, ElementRef, Inject, ViewChild
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRoute, ParamMap, Params, Router
} from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  filter,
  from,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs';

import {
  ConfigurationService,
  IProviderShareableLinkAppConfig,
} from '@sondermind/configuration';
import {
  IAppointmentSlot,
  ProviderCalendarStore,
} from '@sondermind/data-access/public-facing/provider-calendar';
import {
  IProviderCalendarInitSegmentData
} from '@sondermind/features/public-facing/provider-calendar';
import {
  IRequestSessionParams,
  RequestASessionService,
} from '@sondermind/features/public-facing/session-request';
import { LandingPageContact } from '@sondermind/features/support-phone-number-card';
import {
  GtmCustomEvents,
  GtmDataLayerService,
} from '@sondermind/google-tag-manager';
import {
  ICalendarVisibilityConfig,
  IPslRedesignFeatureFlagData,
  LAUNCH_DARKLY_SERVICE,
  LaunchDarklyService,
  LDBooleanFeatureFlags,
  LDJsonFeatureFlags,
} from '@sondermind/launch-darkly';
import { NativeAppContextHelpers } from '@sondermind/native-app-interface';
import { RecaptchaService } from '@sondermind/recaptcha';
import { SegmentService } from '@sondermind/segment';
import { IAPIFetchResponse } from '@sondermind/utilities/models-http';
import {
  ILocation,
  IProviderShareableProfile,
} from '@sondermind/utilities/models-public-facing';
import { ISessionRequestPreferences } from '@sondermind/utilities/models-scheduling';
import { SessionType } from '@sondermind/utilities/models-session';
import { WINDOW } from '@sondermind/utilities/tools';

import {
  IPslSegmentSchema,
  PslSegmentEvents,
} from '../../models/segment/psl-segment-schema.interface';
import { IPslPageLoadEventData } from '../../models/segment/segment-event-data.interface';
import { ITopSpecialty } from '../../models/top-specialty.interface';
import { ShareableLinksHttpService } from '../../shareable-links.httpservice';

const MOBILE_BOTTOM_DRAWER_HEIGHT = 120;
@Component({
  selector: 'landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent {
  providerShareableProfile: IProviderShareableProfile =
    {} as IProviderShareableProfile;
  isValid: boolean = false;
  isLoading: boolean = true;

  therapyMatchFlowUrl: string = 'https://start.sondermind.com';

  @ViewChild('actions', { static: false })
  private actionsCard: ElementRef<HTMLElement>;
  isCalendarScrolledIntoView: boolean;

  calendarSegmentDataSubj: BehaviorSubject<IProviderCalendarInitSegmentData | null> =
    new BehaviorSubject<IProviderCalendarInitSegmentData | null>(null);
  calendarSegmentData$: Observable<IProviderCalendarInitSegmentData | null> =
    this.calendarSegmentDataSubj.asObservable();

  soonestAvailableDate$: Observable<Date | null>;
  paramsShareCode: string = '';
  paramsName: string = '';
  requestSessionQueryParams: IRequestSessionParams = null; // For tracking the user coming from .com site
  topSpecialties: ITopSpecialty[] = [];
  providerPrimaryAddress: ILocation = null;

  queryParamMap!: ParamMap;
  queryParams: Params;

  pslRedesignFf$: Observable<IPslRedesignFeatureFlagData>;

  appointmentSlots$: Observable<IAppointmentSlot[]>;
  isCalendarLoading: boolean = true;
  showProviderCalendar$: Observable<boolean>;

  destroyed$ = new Subject<void>();

  supportPhoneNumberLink: string = LandingPageContact.MATCH_BY_PHONE_LINK;
  supportPhoneNumberDisplay: string = LandingPageContact.MATCH_BY_PHONE_DISPLAY;
  /**
   * Only true when a user schedules via the Native App AI Intake flow.
   */
  isScheduled: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private shareableLinksHttpService: ShareableLinksHttpService,
    private configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    private titleService: Title,
    private metaService: Meta,
    private gtmDataLayerService: GtmDataLayerService,
    private segmentService: SegmentService<IPslSegmentSchema>,
    private requestASessionService: RequestASessionService,
    private recaptchaService: RecaptchaService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: LaunchDarklyService,
    @Inject(WINDOW) private window: Window,
    private router: Router,
    private providerCalendarStore: ProviderCalendarStore,
  ) {}

  ngOnInit(): void {
    this.initializeFeatureFlags();

    combineLatest([this.route.paramMap, this.route.queryParamMap, this.route.queryParams])
      .pipe(take(1))
      .subscribe(([routeParams, queryParamMap, queryParams]) => {
        this.queryParams = queryParams;
        this.queryParamMap = queryParamMap;
        this.therapyMatchFlowUrl = this.configurationService.env.app.flowHost;

        this.paramsShareCode =
          this.requestASessionService.getShareCodeFromParams(routeParams);
        this.paramsName =
          this.requestASessionService.getNameFromParams(routeParams);
        this.requestSessionQueryParams =
          this.requestASessionService.parseQueryParams(queryParamMap);

        this.isScheduled = queryParamMap.get('scheduled') === 'true';
        this.loadProfile(this.paramsShareCode);
      });

    // HACK: This causes the recaptcha icon to appear on the page, before we open the checkout.
    from(this.recaptchaService.executeEvent$('load'));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private redirectoToUrlWithName() {
    const profileName = this.providerShareableProfile.display_name.toLocaleLowerCase().split(' ').join('-');
    this.router.navigate([`/${this.paramsShareCode}`, profileName],
      { queryParams: this.queryParams ? this.queryParams : {} }).catch(() => {});
  }

  private loadProfile(shareCode: string): void {
    this.shareableLinksHttpService
      .fetch$(shareCode)
      .pipe(
        takeUntil(this.destroyed$),
        catchError((error) => of(error)),
      )
      .subscribe((response: IAPIFetchResponse<IProviderShareableProfile>) => {
        if (Object.prototype.hasOwnProperty.call(response, 'error')) {
          this.isLoading = false;
          return;
        }

        this.providerShareableProfile = response.data;
        if (!this.paramsName) {
          this.redirectoToUrlWithName();
        }
        this.gtmDataLayerService.dataLayer = {
          event: GtmCustomEvents.PROVIDER_PAGE_VIEW,
          id: this.providerShareableProfile.id,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          full_name: this.providerShareableProfile.full_name,
        };
        this.updateMetaInfo(
          this.providerShareableProfile.display_name,
          this.providerShareableProfile.certifications,
          this.providerShareableProfile.bio
        );
        this.updateCanonicalUrl();

        // Avoids ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.isValid = true;
          this.isLoading = false;
        }, 0);

        this.topSpecialties = this.sortTopSpecialties(
          this.providerShareableProfile,
        );
        this.providerPrimaryAddress = this.findPrimaryInPersonAddress(
          this.providerShareableProfile,
        );

        this.initializeCalendar(this.providerShareableProfile);

        const isRequestSession = this.queryParamMap.get('requestSession') === 'true';
        if (isRequestSession) {
          this.onRequestSessionButtonClicked();
        }

        this.sendPageViewSegmentEvent();
      });
  }

  private findPrimaryInPersonAddress(
    profile: IProviderShareableProfile,
  ): ILocation {
    return profile.locations.find(
      (location) => location.office_session_default === true,
    );
  }

  // top specialties are always sorted in alphabetical order
  private sortTopSpecialties(
    profile: IProviderShareableProfile,
  ): ITopSpecialty[] {
    const queryParamSpecialties =
      this.queryParamMap.get('specialties')?.split(',') ?? [];

    return profile.top_specialties
      .map((topSpecialty) => {
        const specialty = this.providerShareableProfile.specialties.find(
          (s) => topSpecialty.id === s.id,
        );
        return {
          ...specialty,
          isQueryParam: queryParamSpecialties.includes(specialty.id.toString()),
          priorityOrder: topSpecialty.priority_order,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private initializeFeatureFlags(): void {
    this.pslRedesignFf$ = this.launchDarklyService
      .checkFlagJsonValue$<IPslRedesignFeatureFlagData>(
      LDJsonFeatureFlags.PSL_PAGE_REDESIGN,
    )
      .pipe(shareReplay(1));
  }

  /** Show the provider calendar if they have x available sessions within y weeks from now,
   * where x and y are defined in the calendarVisibilityConfig feature flag. Otherwise, show the connect card.
   */
  private initializeCalendar(profile: IProviderShareableProfile): void {
    this.appointmentSlots$ = this.providerCalendarStore.getAppointmentSlots$(
      this.providerShareableProfile.id,
      this.providerShareableProfile.time_zone,
    ).pipe(
      shareReplay(1),
    );

    this.showProviderCalendar$ = combineLatest([this.pslRedesignFf$, this.appointmentSlots$]).pipe(
      map(([pslRedesignFlag, appointmentSlots]) => {
        const calendarVisibilityConfig = pslRedesignFlag?.calendarVisibilityConfig;
        return this.shouldShowCalendar(profile, calendarVisibilityConfig, appointmentSlots);
      }),
      catchError(() => of(false)),
      tap(() => {
        this.isCalendarLoading = false;
      }),
      shareReplay(1),
    );

    // Get the soonest available date from the earliest appointment slot
    this.soonestAvailableDate$ = this.appointmentSlots$.pipe(
      map((slots) => {
        const sortedSlots = [...slots].sort(
          (a, b) => a.utcStartTime.getTime() - b.utcStartTime.getTime()
        );
        return sortedSlots[0]?.utcStartTime;
      }),
      shareReplay(1),
    );
  }

  private shouldShowCalendar(
    profile: IProviderShareableProfile,
    calendarVisibilityConfig: ICalendarVisibilityConfig,
    appointmentSlots: IAppointmentSlot[],
  ): boolean {
    // if there is no config, don't show the calendar
    if (!calendarVisibilityConfig
      // if the provider is not accepting, don't show the calendar
      || !profile.accepting_clients
    ) {
      return false;
    }

    // if the provider offers in-person sessions and videoOnly is true, don't show the calendar.
    const hasInPersonSessions = profile.session_types_offered.includes(
      SessionType.IN_PERSON,
    );

    if (calendarVisibilityConfig.videoOnly && hasInPersonSessions) {
      return false;
    }

    // if the provider has no default location (in-person or video), don't show the calendar
    const hasDefaultLocation = profile.locations.some(
      (location) => location.office_session_default || location.video_session_default,
    );

    if (!hasDefaultLocation) {
      return false;
    }

    // otherwise, show the calendar when it fits the specs set out by the config
    let applicableSlots = appointmentSlots;

    // Set a future time threshold if configured
    if (calendarVisibilityConfig.weeksAheadWithAvailability > 0) {
      const futureTimeThreshold = new Date(
        Date.now() +
            calendarVisibilityConfig.weeksAheadWithAvailability *
              7 *
              24 *
              60 *
              60 *
              1000,
      );

      applicableSlots = appointmentSlots.filter((slot) => new Date(slot.utcStartTime) < futureTimeThreshold);
    }

    // Are there enough slots that fit the criteria?
    return applicableSlots.length >= calendarVisibilityConfig.minAvailableSlots;
  }

  onCalendarInit(calendarData: IProviderCalendarInitSegmentData): void {
    this.calendarSegmentDataSubj.next(calendarData);
  }

  onNativeAppBookedASession(
    requestedSessionPreferences?: ISessionRequestPreferences,
  ): void {
    const startTime = requestedSessionPreferences?.startTime || null;
    const sessionType = requestedSessionPreferences?.sessionType || null;

    NativeAppContextHelpers.onPslBookedASession(
      this.providerShareableProfile.id,
      startTime,
      sessionType,
    );
  }

  onRequestSessionButtonClicked(
    requestedSessionPreferences?: ISessionRequestPreferences,
  ): void {
    const isNativeApp = NativeAppContextHelpers.isHostedInNativeApp() ;
    if (!this.isScheduled && isNativeApp) {
      this.onNativeAppBookedASession(requestedSessionPreferences);
      return;
    }
    this.requestASessionService.onRequestSessionButtonClicked({
      profile: this.providerShareableProfile,
      providerPrimaryAddress: this.providerPrimaryAddress,
      queryParams: this.requestSessionQueryParams,
      requestedSessionPreferences: {
        startTime: requestedSessionPreferences?.startTime,
        endTime: requestedSessionPreferences?.endTime,
        sessionType: requestedSessionPreferences?.sessionType,
      },
    });
  }

  onMobileDrawerButtonClicked(): void {
    // if calendar is on the page, scroll to it and focus on the first time slot
    // otherwise, open quick checkout modal
    const scrollTarget: HTMLElement = document.getElementById(
      'public-provider-calendar',
    );
    const focusTarget: HTMLElement = document.querySelector('.carousel-time');

    if (scrollTarget && focusTarget) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      focusTarget.focus({ preventScroll: true });
    } else {
      this.onRequestSessionButtonClicked();
    }
  }

  areActionsScrolledIntoView(): void {
    if (this.actionsCard) {
      const rect = this.actionsCard.nativeElement.getBoundingClientRect();
      // If the actions component is bigger than the whole screen on mobile, use the bigger value
      const totalHeight = Math.max(
        this.actionsCard.nativeElement.clientHeight,
        this.window.visualViewport.height,
      );
      const isTallerThanViewportHeight =
        this.actionsCard.nativeElement.clientHeight >
        this.window.visualViewport.height;

      const isTopShown = rect.top >= -totalHeight + MOBILE_BOTTOM_DRAWER_HEIGHT;
      const isBottomShown =
        rect.bottom <= window.innerHeight + MOBILE_BOTTOM_DRAWER_HEIGHT;
      this.isCalendarScrolledIntoView = isTallerThanViewportHeight
        ? isTopShown
        : isTopShown && isBottomShown;
    }
  }

  private sendPageViewSegmentEvent(): void {
    const activeLaunchDarklyFlags: { [key: string]: unknown; } = {};
    this.launchDarklyService
      .getAllFlags$()
      .pipe(
        take(1),
        combineLatestWith(this.showProviderCalendar$),
        switchMap(([flags, showProviderCalendar]) => {
          // For every active FF we want to track on this page, add the key + FF value to the map
          const trackingFlags = [
            LDBooleanFeatureFlags.FIND_PROVIDER_PUBLIC_HEADER_REDIRECT,
            LDBooleanFeatureFlags.PSL_SEND_REQUEST_A_SESSION_EMAIL,
            LDJsonFeatureFlags.PSL_PAGE_REDESIGN,
          ];
          trackingFlags.forEach((flagName) => {
            activeLaunchDarklyFlags[flagName] = flags[flagName] ?? false;
          });

          // Get UTM parameters
          const urlParams = this.route.snapshot.queryParams;
          const utmParams = Object.entries(urlParams).filter(([key, _value]) => key.startsWith('utm'));

          // Get params from directory, if they exist
          const directoryFilters = {
            state: this.queryParamMap.get('state'),
            insurance: this.queryParamMap.get('paymentTag'),
            modalities: this.queryParamMap.get('modalities'),
            specialties: this.queryParamMap.get('specialties'),
            genders: this.queryParamMap.get('genders'),
          };

          // Cut off any query params
          const referringDomain = this.document.referrer.slice(
            0,
            this.document.referrer.indexOf('?'),
          );
          const referringUrl: string =
            this.queryParamMap.get('referringUrl') ?? '';

          const baseEventData = {
            accepting: this.providerShareableProfile.accepting_clients,
            activeLaunchDarklyFlags,
            directoryFilters,
            internalReferringPage: referringUrl,
            isCalendarVisible: showProviderCalendar,
            personaProviderId: this.providerShareableProfile.id,
            referringDomain,
            utm: utmParams,
          } as IPslPageLoadEventData;

          // if we SHOW the calendar, include calendar initialization data.
          const pageLoadData$: Observable<IPslPageLoadEventData> = showProviderCalendar
            ? this.calendarSegmentData$.pipe(
              filter((calendarData) => !!calendarData),
              take(1),
              map((calendarData: IProviderCalendarInitSegmentData) => ({
                ...baseEventData,
                ...calendarData
              })))
            : of(baseEventData);

          return pageLoadData$.pipe(
            switchMap(
              (eventData) => from(
                this.segmentService.track<PslSegmentEvents.PSL_PAGE_VIEW>(
                  PslSegmentEvents.PSL_PAGE_VIEW,
                  eventData
                )
              )
            )
          );
        })
      ).subscribe();
  }

  private updateMetaInfo(
    providerName: string,
    certifications: Array<{ name: string; abbrev: string; }>,
    bio: string,
  ): void {
    const certs: string = certifications?.map((obj) => obj.name).join(', ');
    const title = `${providerName}, ${certs} | SonderMind`;
    // extract the first sentence of the bio;
    const sentence = /[^.!?]*[.!?]/.exec(bio);
    const firstSentenceofBio = sentence ? sentence[0] : bio;
    const first160CharsofBio = firstSentenceofBio
      ? firstSentenceofBio.substring(0, 159)
      : ''; // bio can be null
    const description = `${firstSentenceofBio ?? ''}`;

    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({
      property: 'og:url',
      content: this.window.location.href,
    });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: first160CharsofBio,
    });
    this.metaService.updateTag({
      property: 'twitter:url',
      content: this.window.location.href,
    });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({
      name: 'twitter:description',
      content: first160CharsofBio,
    });
  }

  private updateCanonicalUrl(): void {
    if (!this.paramsShareCode || (!this.paramsName && !this.providerShareableProfile.display_name)) {
      // Don't set canonical if we don't have the necessary data.
      return;
    }
    let canonicalUrl = this.configurationService.env.providerSharableLinkBase ?? 'https://www.sondermind.com/providers';
    let profileName = this.paramsName;
    if (!this.paramsName) {
      profileName = this.providerShareableProfile.display_name.toLocaleLowerCase().split(' ').join('-');
    }
    canonicalUrl = `${canonicalUrl}/${this.paramsShareCode}/${profileName}`;

    let link = this.document.querySelector("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }
}
